import { Capacitor } from '@capacitor/core'
import { useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useMemo } from 'react'
import { useTheme } from 'styled-components'

import { Margin } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { renderText } from '@/modules/shared/helpers/text-rendering'
import { QuestionnaireHeroContent } from '../../components/questionnaire-hero-content/QuestionnaireHeroContent'
import { splitText } from '../../utils/split-question-text'

import type { IQuestionnaireQuestion } from '@healthblocks-io/core'

type Props = {
  question: IQuestionnaireQuestion | null
  onGoBack: () => void
  onGoNext: () => void
  isSaving?: boolean
}

/**
 * The entry page for the questionnaire
 *  - Is shown when the first question is a display question
 * @param question - The questionnaire question data
 * @param onGoBack - The function to call when the user wants to go back
 * @param onGoNext - The function to call when the user wants to go next
 * @returns
 */
export const QuestionnaireEntry = ({ question, onGoBack, onGoNext, isSaving }: Props) => {
  const theme = useTheme()
  const subjectMember = useSubjectMember()

  // Template variables
  // ------------------------------------------------------------------------- /
  const [title, description] = useMemo(() => splitText(question?.text), [question?.text])

  const descriptionElement = useMemo(() => {
    const titleText = title ? renderText(title) : ''
    const descriptionText = description ? renderText(description) : ''
    return [titleText, descriptionText].filter(Boolean).join(' ')
  }, [title, description])

  return (
    <QuestionnaireHeroContent
      title={t({
        id: 'questionnaire.entry.greeting',
        comment: 'Greetings at questionnaire entry page',
        message: 'Hallo {name}',
        values: {
          name: subjectMember?.givenName,
        },
      })}
      description={descriptionElement ?? description}
      image={'/img/questionnaire-entry.jpg'}
      buttons={
        <>
          <Button variant={'Primary'} onClick={onGoNext} isLoading={isSaving}>
            {t({
              id: 'questionnaire.entry.primary.start',
              comment: 'Primary start button at questionnaire entry page',
              message: 'Ga van start',
            })}
          </Button>
          <Margin mb={theme.UI.Spacing.Space2} />

          <Button variant={'Secondary'} onClick={onGoBack}>
            {t({
              id: 'questionnaire.entry.secondary',
              comment: 'Secondary button at questionnaire entry page',
              message: 'Vragenlijst sluiten',
            })}
          </Button>
        </>
      }
      onGoBack={Capacitor.isNativePlatform() ? undefined : onGoBack}
    />
  )
}
